
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  read_at: string | null;
  is_read: boolean;
}

export interface Conversation {
  id: string;
  participant_1: string;
  participant_2: string;
  last_message_id: string | null;
  updated_at: string;
  other_user: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  last_message?: Message;
  unread_count: number;
}

export const useMessages = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Basic pagination controls (non-breaking, optional)
  const PAGE_SIZE = 30;
  const [hasMore, setHasMore] = useState<boolean>(false);
  const currentOffsetRef = useRef(0);

  // Simple in-memory cache per user
  const cacheRef = useRef<{
    userId: string | null;
    data: Conversation[];
    ts: number;
  }>({ userId: null, data: [], ts: 0 });

  // Debounce refetch to avoid hammering on realtime bursts
  const debounceTimeoutRef = useRef<number | null>(null);
  const debounce = (fn: () => void, delay = 400) => {
    if (debounceTimeoutRef.current) {
      window.clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = window.setTimeout(fn, delay);
  };

  // Safe auth context access
  let user = null;
  try {
    const authContext = useAuth();
    user = authContext.user;
  } catch (err) {
    console.error('Auth context error in useMessages:', err);
    setError('Authentication error');
    setLoading(false);
  }

  const mapRpcRowToConversation = (row: any): Conversation => {
    const otherId = row.other_user_id as string;
    const selfId = user?.id as string;

    const lastMsg: Message | undefined = row.last_message_id
      ? {
          id: row.last_message_id,
          sender_id: row.last_message_sender_id,
          recipient_id: row.last_message_sender_id === selfId ? otherId : selfId,
          content: row.last_message_content ?? '',
          created_at: row.last_message_created_at,
          read_at: row.last_message_is_read ? row.last_message_created_at : null,
          is_read: !!row.last_message_is_read,
        }
      : undefined;

    return {
      id: row.conversation_id,
      participant_1: selfId < otherId ? selfId : otherId,
      participant_2: selfId < otherId ? otherId : selfId,
      last_message_id: row.last_message_id ?? null,
      updated_at: row.updated_at,
      other_user: {
        id: row.other_user_id,
        full_name: row.other_user_full_name ?? 'Unknown User',
        avatar_url: row.other_user_avatar_url ?? null,
      },
      last_message: lastMsg,
      unread_count: Number(row.unread_count ?? 0),
    };
  };

  const fetchConversations = async (opts?: { useCache?: boolean; append?: boolean }) => {
    const { useCache = true, append = false } = opts || {};
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    // Serve quickly from cache if fresh (< 60s) and not appending
    const cacheFreshForMs = 60_000;
    const now = Date.now();
    const isCacheValid =
      !append &&
      cacheRef.current.userId === user.id &&
      now - cacheRef.current.ts < cacheFreshForMs &&
      cacheRef.current.data.length > 0;

    if (useCache && isCacheValid) {
      setConversations(cacheRef.current.data);
      setHasMore(cacheRef.current.data.length % PAGE_SIZE === 0);
      setLoading(false);
      return;
    }

    if (!append) setLoading(true);
    setError(null);

    const offset = append ? currentOffsetRef.current : 0;

    try {
      const { data, error: rpcError } = await supabase.rpc('get_user_conversations_optimized', {
        p_user_id: user.id,
        p_limit: PAGE_SIZE,
        p_offset: offset,
      });

      if (rpcError) {
        console.error('Error fetching conversations via RPC:', rpcError);
        setError('Failed to load conversations');
        setLoading(false);
        return;
      }

      const mapped = (data || []).map(mapRpcRowToConversation);

      if (append) {
        setConversations((prev) => {
          const merged = [...prev];
          const existingIds = new Set(prev.map((c) => c.id));
          for (const c of mapped) {
            if (!existingIds.has(c.id)) merged.push(c);
          }
          cacheRef.current = { userId: user.id, data: merged, ts: Date.now() };
          return merged;
        });
        currentOffsetRef.current = offset + PAGE_SIZE;
      } else {
        setConversations(mapped);
        cacheRef.current = { userId: user.id, data: mapped, ts: Date.now() };
        currentOffsetRef.current = PAGE_SIZE;
      }

      setHasMore((data || []).length === PAGE_SIZE);
    } catch (err) {
      console.error('Error in fetchConversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || loading) return;
    await fetchConversations({ useCache: false, append: true });
  };

  const sendMessage = async (recipientId: string, content: string) => {
    if (!user || !content.trim()) return null;

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: recipientId,
          content: content.trim(),
          message_type: 'private',
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        return null;
      }

      fetchConversations({ useCache: false });
      return data;
    } catch (error) {
      console.error('Error in sendMessage:', error);
      return null;
    }
  };

  const markAsRead = async (messageId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', messageId)
        .eq('recipient_id', user.id);

      if (error) console.error('Error marking message as read:', error);
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  const markConversationAsRead = async (otherUserId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('recipient_id', user.id)
        .eq('sender_id', otherUserId)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking conversation as read:', error);
      } else {
        cacheRef.current.ts = 0;
        fetchConversations({ useCache: false });
      }
    } catch (error) {
      console.error('Error in markConversationAsRead:', error);
    }
  };

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    fetchConversations({ useCache: true });

    const channelName = `messages-updates-${user.id}-${Date.now()}-${Math.random()}`;

    // Clean up any existing channels for this user
    const existingChannels = supabase.getChannels();
    const userChannels = existingChannels.filter((ch) => ch.topic.startsWith(`messages-updates-${user.id}-`));
    userChannels.forEach((channel) => { supabase.removeChannel(channel); });

    const debouncedRefresh = () => debounce(() => fetchConversations({ useCache: false }), 500);

    const messagesChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `message_type=eq.private` },
        (payload) => {
          const newMessage = payload.new as Message;
          if (newMessage.sender_id === user.id || newMessage.recipient_id === user.id) {
            debouncedRefresh();
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages', filter: `message_type=eq.private` },
        (payload) => {
          const updatedMessage = payload.new as Message;
          if (updatedMessage.sender_id === user.id || updatedMessage.recipient_id === user.id) {
            debouncedRefresh();
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      if (debounceTimeoutRef.current) window.clearTimeout(debounceTimeoutRef.current);
    };
  }, [user]);

  return {
    conversations,
    loading,
    error,
    sendMessage,
    markAsRead,
    markConversationAsRead,
    refetch: () => fetchConversations({ useCache: false }),
    loadMore,
    hasMore,
  };
};
